import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Clock,
  ChevronRight,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { SkeletonDetail } from '../components/Skeleton';
import { useToast } from '../components/ToastProvider';
import { useAuth } from '../context/AuthContext';
import Card from '../components/Card';
import {
  DocumentsPanel,
  ProgressCard,
  RequestSnapshot,
  TimelineItem,
} from '../features/requests/RequestDetailParts';
import RequestDetailHeader, { RequestStatStrip } from '../features/requests/RequestDetailHeader';
import RequestDetailModals from '../features/requests/RequestDetailModals';
import RequestDetailReviewPanel from '../features/requests/RequestDetailReviewPanel';
import useRequestDetailActions from '../features/requests/useRequestDetailActions';
import useRequestDetailData from '../features/requests/useRequestDetailData';
import {
  fmtCurrencyOrNull as formatCurrency,
  fmtDateOrNull as formatDate,
  fmtDateTimeOrEmpty as formatDateTime,
  formatYesNo,
} from '../utils/format';

export default function RequestDetail() {
  const { id } = useParams();
  const { showToast } = useToast();
  const { user } = useAuth();

  const {
    error,
    loading,
    refresh: fetchRequest,
    request,
  } = useRequestDetailData(id);

  const {
    actionLoading,
    canArchive,
    canCreateContract,
    canDelete,
    canEdit,
    canReviewLegal,
    canReviewSecurity,
    canSubmit,
    canUnarchive,
    canUploadDocs,
    canWithdraw,
    docUpload,
    handleArchive,
    handleCreateContract,
    handleDelete,
    handleDocumentUpload,
    handleRerunFirstPass,
    handleReview,
    handleSubmit,
    handleUnarchive,
    handleWithdraw,
    hasElevatedAccess,
    isArchived,
    openReviewModal,
    reviewAction,
    reviewLane,
    reviewNotes,
    setDocUpload,
    setReviewAction,
    setReviewLane,
    setReviewNotes,
    setShowDeleteModal,
    setShowReviewModal,
    showDeleteModal,
    showReviewModal,
    uploadingDoc,
  } = useRequestDetailActions({
    id,
    refresh: fetchRequest,
    request,
    showToast,
    user,
  });

  if (loading) return <SkeletonDetail />;

  if (error) {
    return (
      <div className="canvas-inner animate-fade-in">
        <div className="page-eyebrow" style={{ marginBottom: 16 }}>
          <Link to="/tprm" className="row" style={{ gap: 4, color: 'inherit', textDecoration: 'none' }}>
            <ArrowLeft className="w-3 h-3" />
            Requests
          </Link>
        </div>
        <div className="callout red" style={{ padding: '16px 20px' }}>
          <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-red-500)', flexShrink: 0, marginTop: 1 }} />
          <div className="callout-body">
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{error}</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Unable to load the request details.</div>
            <div style={{ marginTop: 12 }}>
              <Link to="/tprm" className="btn outline sm">
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to Requests
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const firstPass = request.firstPassReview || null;
  const riskRating = request.recommendedRiskRating || request.riskRating || firstPass?.recommendedRiskRating;
  const legalFlags = firstPass?.legal?.flags || [];
  const securityFlags = firstPass?.security?.flags || [];

  return (
    <div className="canvas-inner animate-fade-in">
      {/* Breadcrumb (sits above the header card so it doesn't compete
          with the ribbon). Matches the contract-detail crumb pattern. */}
      <div className="row" style={{ gap: 6, fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
        <Link to="/tprm" className="row" style={{ gap: 4, color: 'inherit', textDecoration: 'none' }}>
          <ArrowLeft className="w-3 h-3" />
          TPRM
        </Link>
        <ChevronRight className="w-3 h-3" style={{ opacity: 0.4 }} />
        <span style={{ color: 'var(--text-secondary)' }}>Requests</span>
      </div>

      <RequestDetailHeader
        actionLoading={actionLoading}
        canArchive={canArchive}
        canCreateContract={canCreateContract}
        canDelete={canDelete}
        canEdit={canEdit}
        canSubmit={canSubmit}
        canUnarchive={canUnarchive}
        canWithdraw={canWithdraw}
        handleArchive={handleArchive}
        handleCreateContract={handleCreateContract}
        handleSubmit={handleSubmit}
        handleUnarchive={handleUnarchive}
        handleWithdraw={handleWithdraw}
        isArchived={isArchived}
        onDelete={() => setShowDeleteModal(true)}
        request={request}
      />

      <RequestStatStrip
        request={request}
        riskRating={riskRating}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
      />

      {/* Changes requested — tell the requester exactly which lane asked and
          what they said, with the fix-it path one click away. Previously the
          guidance was buried at the bottom of a non-default review tab. */}
      {request.status === 'changes_requested' && canEdit && (
        <div className="callout yellow" style={{ marginBottom: 20 }}>
          <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-yellow-500)', flexShrink: 0, marginTop: 1 }} />
          <div className="callout-body" style={{ minWidth: 0, flex: 1 }}>
            <strong>Changes requested</strong>
            <div style={{ marginTop: 6, display: 'grid', gap: 6, fontSize: 12 }}>
              {request.legalReviewStatus === 'changes_requested' && (
                <div>
                  <span style={{ fontWeight: 600 }}>Legal:</span>{' '}
                  {request.legalReviewNotes || 'See the Legal review tab for details.'}
                </div>
              )}
              {request.securityReviewStatus === 'changes_requested' && (
                <div>
                  <span style={{ fontWeight: 600 }}>Security:</span>{' '}
                  {request.securityReviewNotes || 'See the Security review tab for details.'}
                </div>
              )}
            </div>
            <div style={{ marginTop: 10 }}>
              <Link to={`/tprm/${request.id}/edit`} className="btn primary sm">
                Edit &amp; resubmit
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Linked Contract */}
      {request.contractId && (
        <div className="callout blue" style={{ marginBottom: 20 }}>
          <FileText className="w-4 h-4" style={{ color: 'var(--color-blue-500)', flexShrink: 0, marginTop: 1 }} />
          <div className="callout-body">
            <strong>Contract created</strong>
            <div style={{ marginTop: 2, fontSize: 12, color: 'var(--text-secondary)' }}>
              A contract has been created from this request.
            </div>
          </div>
          <Link to={`/contracts/${request.contractId}`} className="btn primary sm" style={{ marginLeft: 'auto' }}>
            View contract
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      )}

      <div className="detail-grid">
        {/* Main Content */}
        <div className="stack-lg">
          <RequestDetailReviewPanel
            actionLoading={actionLoading}
            canReviewLegal={canReviewLegal}
            canReviewSecurity={canReviewSecurity}
            firstPass={firstPass}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            formatDateTime={formatDateTime}
            formatYesNo={formatYesNo}
            handleRerunFirstPass={handleRerunFirstPass}
            hasElevatedAccess={hasElevatedAccess}
            legalFlags={legalFlags}
            openReviewModal={openReviewModal}
            request={request}
            securityFlags={securityFlags}
          />
        </div>

        {/* Sidebar */}
        <div className="detail-rail">
          <ProgressCard request={request} formatDate={formatDate} />

          <RequestSnapshot
            request={request}
            formatDate={formatDate}
          />

          <DocumentsPanel
            request={request}
            canUploadDocs={canUploadDocs}
            hasElevatedAccess={hasElevatedAccess}
            docUpload={docUpload}
            setDocUpload={setDocUpload}
            uploadingDoc={uploadingDoc}
            handleDocumentUpload={handleDocumentUpload}
            formatDateTime={formatDateTime}
          />

          <Card title="History" icon={Clock}>
            {request.statusHistory && request.statusHistory.length > 0 ? (
              <div>
                {request.statusHistory.map((history, index) => (
                  <TimelineItem
                    key={history.id}
                    history={history}
                    isLast={index === request.statusHistory.length - 1}
                    formatDateTime={formatDateTime}
                  />
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                No history available
              </div>
            )}
          </Card>
        </div>
      </div>

      <RequestDetailModals
        actionLoading={actionLoading}
        handleDelete={handleDelete}
        handleReview={handleReview}
        reviewAction={reviewAction}
        reviewLane={reviewLane}
        reviewNotes={reviewNotes}
        setReviewAction={setReviewAction}
        setReviewLane={setReviewLane}
        setReviewNotes={setReviewNotes}
        setShowDeleteModal={setShowDeleteModal}
        setShowReviewModal={setShowReviewModal}
        showDeleteModal={showDeleteModal}
        showReviewModal={showReviewModal}
      />
    </div>
  );
}
